import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, CheckCircle2, XCircle, BookOpen, Map, ShieldAlert, Calendar, HeartPulse, ArrowRight, Clock, ShieldCheck, Star } from 'lucide-react';
import { PawPrint } from './components/PawPrint';
import { AccordionItem } from './components/Accordion';
import { LegalModal } from './components/LegalModal';
import { initMetaPixel, trackPageView, trackAddToCart } from './utils/metaPixel';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function App() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  useEffect(() => {
    initMetaPixel();
    trackPageView();
  }, []);
  const scrollToOffer = () => {
    document.getElementById('offer-section')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-cream font-sans text-charcoal overflow-x-hidden selection:bg-amber/30" data-id="element-0">
      {/* Decorative Background Elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0" data-id="element-1">
        <PawPrint className="absolute top-20 left-10 w-32 h-32 text-amber -rotate-12" opacity={0.03} data-id="element-2" />
        <PawPrint className="absolute top-1/4 right-10 w-48 h-48 text-sage rotate-45" opacity={0.03} data-id="element-3" />
        <PawPrint className="absolute bottom-1/3 left-20 w-40 h-40 text-amber rotate-12" opacity={0.03} data-id="element-4" />
        <PawPrint className="absolute bottom-20 right-20 w-32 h-32 text-sage -rotate-45" opacity={0.03} data-id="element-5" />
      </div>

      <main className="relative z-10" data-id="element-6">
        {/* HERO SECTION */}
        <section className="pt-12 pb-10 md:pt-32 md:pb-24 px-5 md:px-6 max-w-7xl mx-auto" data-id="element-7">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center" data-id="element-8">
            <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-2xl" data-id="element-9">
              <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full bg-amber/10 text-amber-dark font-medium text-sm mb-4 md:mb-6" data-id="element-10">
                <Star size={16} className="fill-amber-dark" data-id="element-11" />
                <span data-id="element-12">Para hogares con 2 o más gatos</span>
              </motion.div>

              <motion.h1 variants={fadeInUp} className="font-serif text-3xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 md:mb-6 text-charcoal" data-id="element-13">
                Transformá el Caos en Calma: El Sistema{' '}
                <span className="text-amber-dark italic" data-id="element-14">"Paz Felina"</span>{' '}
                para Hogares con Varios Gatos
              </motion.h1>

              <motion.p variants={fadeInUp} className="text-base md:text-xl text-charcoal-light mb-6 md:mb-8 leading-relaxed" data-id="element-15">
                Aprendé a decodificar la jerarquía de tus gatos y terminá con
                los bufidos, las corridas y la tensión en menos de 7 días.
              </motion.p>

              <motion.div variants={fadeInUp} data-id="element-16">
                <button onClick={scrollToOffer} className="w-full md:w-auto bg-amber hover:bg-amber-dark text-white font-bold text-base md:text-lg px-6 py-3.5 md:px-8 md:py-4 rounded-xl shadow-soft hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-3 group" data-id="element-17">
                  ¡QUIERO PAZ EN MI CASA AHORA!
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} data-id="element-18" />
                </button>
                <p className="text-sm text-charcoal-light mt-3 flex items-center gap-2 justify-center md:justify-start" data-id="element-19">
                  Acceso inmediato
                </p>
              </motion.div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 1,
            ease: 'easeOut'
          }} className="relative" data-id="element-20">
              <div className="absolute inset-0 bg-amber/20 rounded-[2rem] transform rotate-3 scale-105 -z-10 blur-xl" data-id="element-21"></div>
              <img src="https://cdn.magicpatterns.com/uploads/jbbPeZSj5LeumgmtS6SNMB/moockup.png" alt="Domina la Convivencia Multigato en Tablet" className="w-full h-auto rounded-2xl shadow-2xl relative z-10" data-id="element-22" />
            </motion.div>
          </div>
        </section>

        {/* OPENING HOOK */}
        <section className="py-10 md:py-20 bg-white px-5 md:px-6" data-id="element-23">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={fadeInUp} className="max-w-3xl mx-auto text-center" data-id="element-24">
            <p className="font-serif text-xl md:text-3xl leading-relaxed text-charcoal italic" data-id="element-25">
              "Bajás a la cocina a las 3 de la mañana y los escuchás: ese
              gruñido sordo que precede al desastre. El choque de garras, los
              pelos volando y el grito que te corta la respiración."
            </p>
            <p className="mt-5 md:mt-8 text-base md:text-lg text-charcoal-light" data-id="element-26">
              Tu casa ya no se siente como un refugio. Se siente como una zona
              de guerra donde tenés que caminar con cuidado para no activar una
              pelea entre tus propios gatos.
            </p>
          </motion.div>
        </section>

        {/* THE PROBLEM */}
        <section className="py-10 md:py-20 px-5 md:px-6 max-w-4xl mx-auto" data-id="element-27">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-28">
            <motion.h2 variants={fadeInUp} className="font-serif text-2xl md:text-4xl font-bold text-center mb-8 md:mb-12" data-id="element-29">
              Vivir con gatos que no se llevan bien es agotador.
            </motion.h2>

            <div className="grid md:grid-cols-2 gap-4 md:gap-8 mb-8 md:mb-16" data-id="element-30">
              <motion.div variants={fadeInUp} className="bg-cream-dark p-5 md:p-8 rounded-2xl" data-id="element-31">
                <p className="text-base md:text-lg text-charcoal-light leading-relaxed" data-id="element-32">
                  Sentís culpa por el gato "agresor" y angustia por el que vive
                  escondido bajo la cama. Te da miedo irte a trabajar y dejarlos
                  solos.
                </p>
              </motion.div>
              <motion.div variants={fadeInUp} className="bg-cream-dark p-5 md:p-8 rounded-2xl" data-id="element-33">
                <p className="text-base md:text-lg text-charcoal-light leading-relaxed" data-id="element-34">
                  Probaste de todo: Feliway, gritarles, separarlos un rato, o
                  simplemente esperar a que "se acostumbren". Pero nada funciona
                  porque no estás atacando la raíz del problema:{' '}
                  <strong className="text-amber-dark" data-id="element-35">
                    el territorio y la jerarquía.
                  </strong>
                </p>
              </motion.div>
            </div>

            {/* WHY WHAT YOU TRIED DIDN'T WORK */}
            <motion.div variants={fadeInUp} className="bg-white rounded-2xl md:rounded-3xl p-5 md:p-12 shadow-soft border border-red-100" data-id="element-36">
              <h3 className="font-serif text-xl md:text-2xl font-bold mb-5 md:mb-8 flex items-center gap-3 text-red-800" data-id="element-37">
                <AlertTriangle className="text-red-500 flex-shrink-0" size={22} data-id="element-38" />
                Por qué lo que probaste no funcionó:
              </h3>
              <ul className="space-y-4 md:space-y-6" data-id="element-39">
                {['Castigarlos solo aumenta su nivel de cortisol y los hace asociar el estrés con el otro gato.', 'Forzar el contacto visual genera un trauma territorial que puede tardar meses en sanar.', 'Tratarlos como "humanos chiquitos" ignora que sus necesidades de supervivencia son 100% instintivas.', 'Esperar a que el tiempo lo cure todo suele terminar en una herida grave o una visita de urgencia al veterinario.'].map((item, i) => <li key={i} className="flex gap-3 items-start" data-id="element-40">
                    <XCircle className="text-red-400 flex-shrink-0 mt-0.5" size={18} data-id="element-41" />
                    <span className="text-charcoal-light text-base md:text-lg" data-id="element-42">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </motion.div>
          </motion.div>
        </section>

        {/* THE UNIQUE MECHANISM */}
        <section className="py-12 md:py-24 bg-sage text-white px-5 md:px-6 relative overflow-hidden" data-id="element-43">
          <PawPrint className="absolute top-0 right-0 w-64 h-64 text-white -translate-y-1/2 translate-x-1/4" opacity={0.1} data-id="element-44" />

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="max-w-4xl mx-auto text-center relative z-10" data-id="element-45">
            <h2 className="font-serif text-2xl md:text-5xl font-bold mb-4 md:mb-8" data-id="element-46">
              El Método de Jerarquía de 3 Niveles
            </h2>
            <p className="text-lg md:text-2xl font-serif italic mb-4 md:mb-8 text-sage-light" data-id="element-47">
              Los gatos no necesitan democracia, necesitan claridad.
            </p>
            <p className="text-base md:text-xl leading-relaxed text-white/90 max-w-3xl mx-auto" data-id="element-48">
              Este sistema te enseña a rediseñar tu casa y tu comportamiento
              para que cada gato entienda su rol sin necesidad de recurrir a la
              violencia. No se trata de "entrenarlos", se trata de hablar su
              mismo idioma de seguridad territorial.
            </p>
          </motion.div>
        </section>

        {/* WHAT YOU GET */}
        <section className="py-10 md:py-20 px-5 md:px-6 max-w-5xl mx-auto" data-id="element-49">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} data-id="element-50">
            <motion.h2 variants={fadeInUp} className="font-serif text-2xl md:text-4xl font-bold text-center mb-8 md:mb-16" data-id="element-51">
              ¿Qué incluye el sistema completo?
            </motion.h2>

            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-6" data-id="element-52">
              {[{
              icon: BookOpen,
              title: 'La Guía Maestra',
              desc: '"Domina la Convivencia Multigato" (PDF completo).'
            }, {
              icon: Star,
              title: 'Matriz de Roles',
              desc: 'Identificación precisa de Roles Felinos.'
            }, {
              icon: Map,
              title: 'Mapa de Territorio',
              desc: 'Optimizado para evitar puntos de choque.'
            }, {
              icon: ShieldAlert,
              title: 'Protocolo de Interrupción',
              desc: 'Cómo frenar peleas sin salir lastimado vos.'
            }, {
              icon: Calendar,
              title: 'Rutinas de 15 Minutos',
              desc: 'Calendario exacto para bajar la ansiedad diaria.'
            }, {
              icon: HeartPulse,
              title: 'Checklist de Salud',
              desc: 'Detección temprana de estrés y problemas.'
            }].map((item, i) => <motion.div key={i} variants={fadeInUp} className="bg-white p-4 md:p-6 rounded-xl md:rounded-2xl shadow-soft border border-amber/10 hover:border-amber/30 transition-colors" data-id="element-53">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-amber/10 rounded-lg md:rounded-xl flex items-center justify-center mb-3 md:mb-4 text-amber-dark" data-id="element-54">
                    <item.icon size={20} data-id="element-55" />
                  </div>
                  <h3 className="font-bold text-sm md:text-lg mb-1 md:mb-2" data-id="element-56">
                    {item.title}
                  </h3>
                  <p className="text-charcoal-light text-xs md:text-base" data-id="element-57">
                    {item.desc}
                  </p>
                </motion.div>)}
            </div>
          </motion.div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-10 md:py-20 bg-cream-dark px-5 md:px-6" data-id="element-58">
          <div className="max-w-4xl mx-auto" data-id="element-59">
            <motion.h2 initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="font-serif text-2xl md:text-4xl font-bold text-center mb-8 md:mb-16" data-id="element-60">
              Cómo funciona en la práctica
            </motion.h2>

            <div className="space-y-4 md:space-y-8" data-id="element-61">
              {[{
              step: '1',
              text: 'Identificás los roles de tus gatos con nuestra matriz de comportamiento.'
            }, {
              step: '2',
              text: 'Ajustás el territorio (comida, arena y alturas) según el Mapa de Optimización.'
            }, {
              step: '3',
              text: 'Implementás la Escalera de Introducción para resetear la relación desde cero.'
            }].map((item, i) => <motion.div key={i} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeInUp} className="flex items-center gap-4 md:gap-6 bg-white p-4 md:p-8 rounded-xl md:rounded-2xl shadow-soft" data-id="element-62">
                  <div className="w-12 h-12 md:w-16 md:h-16 flex-shrink-0 bg-amber text-white font-serif text-xl md:text-2xl font-bold rounded-full flex items-center justify-center" data-id="element-63">
                    {item.step}
                  </div>
                  <p className="text-base md:text-xl text-charcoal font-medium" data-id="element-64">
                    {item.text}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* WHO IT IS FOR / NOT FOR */}
        <section className="py-10 md:py-20 px-5 md:px-6 max-w-5xl mx-auto" data-id="element-65">
          <div className="grid md:grid-cols-2 gap-5 md:gap-12" data-id="element-66">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="bg-white p-5 md:p-8 rounded-2xl md:rounded-3xl shadow-soft border-t-4 border-sage" data-id="element-67">
              <h3 className="font-serif text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center gap-2" data-id="element-68">
                <CheckCircle2 className="text-sage flex-shrink-0" size={22} data-id="element-69" />
                Este sistema ES para vos si:
              </h3>
              <ul className="space-y-3 md:space-y-4" data-id="element-70">
                {['Dueños de dos o más gatos que se bufan o se pelean a diario.', 'Personas que acaban de adoptar un nuevo gato y la presentación salió mal.', 'Hogares donde un gato vive "viviendo en las alturas" o escondido por miedo.', 'Amantes de los gatos en Argentina que quieren recuperar la paz en su living.'].map((item, i) => <li key={i} className="flex gap-3 items-start" data-id="element-71">
                    <div className="w-1.5 h-1.5 rounded-full bg-sage mt-2 flex-shrink-0" data-id="element-72" />
                    <span className="text-charcoal-light text-sm md:text-base" data-id="element-73">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeInUp} className="bg-white p-5 md:p-8 rounded-2xl md:rounded-3xl shadow-soft border-t-4 border-charcoal/20" data-id="element-74">
              <h3 className="font-serif text-xl md:text-2xl font-bold mb-4 md:mb-6 flex items-center gap-2" data-id="element-75">
                <XCircle className="text-charcoal/40 flex-shrink-0" size={22} data-id="element-76" />
                NO es para vos si:
              </h3>
              <ul className="space-y-3 md:space-y-4" data-id="element-77">
                {['Personas que buscan una solución mágica sin dedicar 15 minutos al día a sus mascotas.', 'Dueños de un solo gato (a menos que planeen adoptar otro pronto).', 'Casos de agresión por enfermedades físicas graves (siempre consultá al veterinario primero).'].map((item, i) => <li key={i} className="flex gap-3 items-start" data-id="element-78">
                    <div className="w-1.5 h-1.5 rounded-full bg-charcoal/30 mt-2 flex-shrink-0" data-id="element-79" />
                    <span className="text-charcoal-light text-sm md:text-base" data-id="element-80">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </section>

        {/* PROOF / BELIEVABILITY */}
        <section className="py-12 md:py-20 bg-amber text-white px-5 md:px-6" data-id="element-81">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="max-w-3xl mx-auto text-center" data-id="element-82">
            <Clock size={36} className="mx-auto mb-4 md:mb-6 text-white/80" data-id="element-83" />
            <h2 className="font-serif text-2xl md:text-4xl font-bold mb-4 md:mb-8" data-id="element-84">
              Lo que cambia en 7 días:
            </h2>
            <p className="text-base md:text-2xl leading-relaxed font-medium" data-id="element-85">
              Vas a pasar de separar peleas a gritos a ver a tus gatos compartir
              el mismo ambiente en calma, reconociendo sus espacios y eliminando
              la tensión constante que hoy se respira en tu casa.
            </p>
          </motion.div>
        </section>

        {/* FAQ */}
        <section className="py-10 md:py-20 px-5 md:px-6 max-w-3xl mx-auto" data-id="element-86">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="font-serif text-2xl md:text-4xl font-bold text-center mb-6 md:mb-12" data-id="element-87">
            Preguntas Frecuentes
          </motion.h2>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white rounded-2xl md:rounded-3xl p-5 md:p-10 shadow-soft" data-id="element-88">
            {[{
            q: '¿Sirve si mis gatos se pelean hace años?',
            a: 'Sí, el sistema de "reseteo" sirve para cualquier etapa.'
          }, {
            q: '¿Tengo que comprar muebles caros?',
            a: 'No, la mayoría de los ajustes se hacen con lo que ya tenés.'
          }, {
            q: '¿Cuánto tiempo me va a llevar?',
            a: 'Solo necesitás 15 minutos de enfoque por día.'
          }, {
            q: '¿Es un libro físico?',
            a: 'No, es un producto digital de acceso inmediato.'
          }, {
            q: '¿Qué pasa si mi gato es muy agresivo?',
            a: 'En la guía aprenderás a diferenciar juego de agresión y a intervenir de forma segura.'
          }, {
            q: '¿Puedo pagarlo en pesos argentinos?',
            a: 'Sí, a través de nuestra plataforma de pago local.'
          }].map((faq, i) => <AccordionItem key={i} question={faq.q} answer={faq.a} data-id="element-89" />)}
          </motion.div>
        </section>

        {/* OFFER + PRICE BLOCK (CTA) */}
        <section id="offer-section" className="py-12 md:py-24 px-5 md:px-6 bg-white relative overflow-hidden" data-id="element-90">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-amber/5 pointer-events-none" data-id="element-91"></div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="max-w-4xl mx-auto bg-cream-dark rounded-2xl md:rounded-[2.5rem] p-6 md:p-16 text-center shadow-xl relative z-10 border border-amber/20" data-id="element-92">
            <motion.h2 variants={fadeInUp} className="font-serif text-2xl md:text-5xl font-bold mb-4 md:mb-6 text-charcoal" data-id="element-93">
              Recuperá la armonía de tu hogar hoy mismo.
            </motion.h2>

            <motion.p variants={fadeInUp} className="text-base md:text-xl text-charcoal-light mb-6 md:mb-10 max-w-2xl mx-auto" data-id="element-94">
              Llevate el sistema completo{' '}
              <strong className="text-charcoal font-serif italic" data-id="element-95">
                "Domina la Convivencia Multigato"
              </strong>{' '}
              con todos sus bonos de implementación inmediata.
            </motion.p>

            <motion.div variants={fadeInUp} className="bg-white inline-block px-6 py-4 md:px-10 md:py-6 rounded-xl md:rounded-2xl shadow-sm mb-6 md:mb-10 border border-amber/10" data-id="element-96">
              <p className="text-xs md:text-sm text-charcoal-light uppercase tracking-wider font-bold mb-1 md:mb-2" data-id="element-97">
                Inversión única
              </p>
              <div className="flex items-center justify-center gap-2" data-id="element-98">
                <span className="text-4xl md:text-5xl font-bold text-amber-dark" data-id="element-99">
                  $8.639
                </span>
              </div>
              <p className="text-xs md:text-sm text-charcoal-light mt-1 md:mt-2" data-id="element-100">
                Pesos argentinos · Pago único
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} data-id="element-101">
              <a href="https://amigospeludosarg.shop/cart/49045744353522:1" target="_blank" rel="noopener noreferrer" onClick={() => trackAddToCart()} className="w-full md:w-auto bg-amber hover:bg-amber-dark text-white font-bold text-lg md:text-xl px-8 py-4 md:px-10 md:py-5 rounded-xl shadow-soft hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex items-center justify-center gap-3 mx-auto group no-underline" data-id="element-102">
                ¡QUIERO PAZ EN MI CASA AHORA!
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={20} data-id="element-103" />
              </a>
              <p className="mt-4 md:mt-6 text-charcoal-light text-sm md:text-base flex items-center justify-center gap-2" data-id="element-104">
                <ShieldCheck className="text-sage flex-shrink-0" size={18} data-id="element-105" />
                Acceso inmediato tras el pago. Tu hogar vuelve a ser un refugio
                hoy.
              </p>
            </motion.div>
          </motion.div>
        </section>

        {/* Footer */}
        <footer className="py-6 border-t border-charcoal/5" data-id="element-106">
          <div className="max-w-4xl mx-auto px-5 flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-charcoal-light/60" data-id="element-107">
            <p data-id="element-108">
              © {new Date().getFullYear()} Paz Felina. Todos los derechos
              reservados.
            </p>
            <div className="flex items-center gap-4" data-id="element-109">
              <button onClick={() => setShowPrivacy(true)} className="hover:text-charcoal transition-colors underline underline-offset-2" data-id="element-110">
                Política de Privacidad
              </button>
              <span className="text-charcoal/20" data-id="element-111">|</span>
              <button onClick={() => setShowTerms(true)} className="hover:text-charcoal transition-colors underline underline-offset-2" data-id="element-112">
                Términos y Condiciones
              </button>
            </div>
          </div>
        </footer>
      </main>

      <LegalModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Política de Privacidad" data-id="element-113">
        <p data-id="element-114">
          <strong data-id="element-115">Última actualización:</strong> Abril 2026
        </p>
        <p data-id="element-116">
          En Paz Felina, respetamos tu privacidad y nos comprometemos a proteger
          tus datos personales. Esta política describe cómo recopilamos, usamos
          y protegemos tu información.
        </p>
        <h3 className="font-serif font-bold text-charcoal text-base md:text-lg mt-2" data-id="element-117">
          Información que recopilamos
        </h3>
        <p data-id="element-118">
          Recopilamos únicamente la información necesaria para procesar tu
          compra: nombre, dirección de correo electrónico y datos de pago. No
          almacenamos datos de tarjetas de crédito; estos son procesados de
          forma segura por nuestra plataforma de pago.
        </p>
        <h3 className="font-serif font-bold text-charcoal text-base md:text-lg mt-2" data-id="element-119">
          Uso de la información
        </h3>
        <p data-id="element-120">
          Tu información se utiliza exclusivamente para: procesar tu pedido,
          enviarte el producto digital adquirido, y comunicarnos contigo en caso
          de ser necesario respecto a tu compra.
        </p>
        <h3 className="font-serif font-bold text-charcoal text-base md:text-lg mt-2" data-id="element-121">
          Protección de datos
        </h3>
        <p data-id="element-122">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información personal contra acceso no autorizado,
          alteración o destrucción.
        </p>
        <h3 className="font-serif font-bold text-charcoal text-base md:text-lg mt-2" data-id="element-123">
          Tus derechos
        </h3>
        <p data-id="element-124">
          Podés solicitar acceso, rectificación o eliminación de tus datos
          personales en cualquier momento contactándonos por correo electrónico.
        </p>
        <h3 className="font-serif font-bold text-charcoal text-base md:text-lg mt-2" data-id="element-125">
          Cookies
        </h3>
        <p data-id="element-126">
          Nuestro sitio puede utilizar cookies para mejorar tu experiencia de
          navegación. Podés configurar tu navegador para rechazarlas si lo
          preferís.
        </p>
      </LegalModal>

      <LegalModal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Términos y Condiciones" data-id="element-127">
        <p data-id="element-128">
          <strong data-id="element-129">Última actualización:</strong> Abril 2026
        </p>
        <p data-id="element-130">
          Al adquirir el producto digital "Domina la Convivencia Multigato",
          aceptás los siguientes términos y condiciones.
        </p>
        <h3 className="font-serif font-bold text-charcoal text-base md:text-lg mt-2" data-id="element-131">
          Producto digital
        </h3>
        <p data-id="element-132">
          El producto consiste en una guía digital en formato PDF y materiales
          complementarios. El acceso se otorga de forma inmediata tras confirmar
          el pago.
        </p>
        <h3 className="font-serif font-bold text-charcoal text-base md:text-lg mt-2" data-id="element-133">
          Uso personal
        </h3>
        <p data-id="element-134">
          El contenido adquirido es para uso personal y no puede ser
          redistribuido, revendido ni compartido públicamente sin autorización
          previa.
        </p>
        <h3 className="font-serif font-bold text-charcoal text-base md:text-lg mt-2" data-id="element-135">
          Política de reembolso
        </h3>
        <p data-id="element-136">
          Debido a la naturaleza digital del producto, no se aceptan
          devoluciones una vez entregado el acceso. Si tenés algún inconveniente
          técnico para acceder al material, contactanos y te ayudaremos a
          resolverlo.
        </p>
        <h3 className="font-serif font-bold text-charcoal text-base md:text-lg mt-2" data-id="element-137">
          Descargo de responsabilidad
        </h3>
        <p data-id="element-138">
          La información proporcionada en esta guía es de carácter educativo y
          no reemplaza la consulta con un veterinario profesional. Ante
          problemas de salud o agresión severa, siempre consultá a un
          profesional.
        </p>
        <h3 className="font-serif font-bold text-charcoal text-base md:text-lg mt-2" data-id="element-139">
          Modificaciones
        </h3>
        <p data-id="element-140">
          Nos reservamos el derecho de modificar estos términos en cualquier
          momento. Las modificaciones serán efectivas desde su publicación en
          este sitio.
        </p>
      </LegalModal>
    </div>;
}